import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../services/axiosInstance";

export const fetchKbs = createAsyncThunk("knowledge/fetchKbs", async () => {
  const { data } = await axiosInstance.get("/ai/kb");
  return data;
});

export const createKb = createAsyncThunk("knowledge/createKb", async (payload) => {
  const { data } = await axiosInstance.post("/ai/kb", payload);
  return data;
});

export const updateKb = createAsyncThunk("knowledge/updateKb", async ({ id, ...payload }) => {
  const { data } = await axiosInstance.patch(`/ai/kb/${id}`, payload);
  return data;
});

export const fetchDocuments = createAsyncThunk("knowledge/fetchDocuments", async (kbId) => {
  const { data } = await axiosInstance.get(`/ai/kb/${kbId}/documents`);
  return { kbId, documents: data };
});

export const ingestUrl = createAsyncThunk(
  "knowledge/ingestUrl",
  async ({ kbId, url }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/ai/kb/${kbId}/documents`, { url });
      return { kbId, document: data };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "URL ingest failed");
    }
  }
);

export const fetchDocumentStatus = createAsyncThunk(
  "knowledge/fetchDocumentStatus",
  async (docId) => {
    const { data } = await axiosInstance.get(`/ai/documents/${docId}`);
    return data;
  }
);

export const reingestDocument = createAsyncThunk(
  "knowledge/reingestDocument",
  async (docId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/ai/documents/${docId}/reingest`);
      return data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "Reingest failed");
    }
  }
);

export const deleteDocument = createAsyncThunk(
  "knowledge/deleteDocument",
  async ({ docId, kbId }) => {
    await axiosInstance.delete(`/ai/documents/${docId}`);
    return { docId, kbId };
  }
);

export const fetchChunks = createAsyncThunk(
  "knowledge/fetchChunks",
  async ({ docId, page = 1 }) => {
    const { data } = await axiosInstance.get(`/ai/documents/${docId}/chunks`, {
      params: { page, limit: 20 },
    });
    return { docId, ...data };
  }
);

export const sendChat = createAsyncThunk(
  "knowledge/sendChat",
  async ({ message, messages, knowledgeBaseIds, conversationId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post("/ai/chat", {
        // Send full history so the backend has context for follow-ups.
        // messages is preferred; fall back to single-message shape for callers that omit it.
        messages: messages ?? [{ role: "user", content: message }],
        knowledgeBaseIds,
        conversationId,
      });
      return data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "Chat failed");
    }
  }
);

function normalizeKb(raw) {
  return raw?.kb ?? raw;
}
function normalizeKbs(raw) {
  return raw?.kbs ?? raw ?? [];
}
function normalizeDocs(raw) {
  return raw?.documents ?? raw ?? [];
}
function normalizeDoc(raw) {
  return raw?.document ?? raw;
}

const knowledgeSlice = createSlice({
  name: "knowledge",
  initialState: {
    kbs: [],
    kbsStatus: "idle",
    documents: {},     // kbId -> Document[]
    docsStatus: {},    // kbId -> "idle"|"loading"|"done"|"error"
    chunks: {},        // docId -> { items, total, page }
    chunksLoading: {},
    chat: [],
    conversationId: null,
    chatLoading: false,
    chatError: null,
  },
  reducers: {
    clearChat(state) {
      state.chat = [];
      state.conversationId = null;
      state.chatError = null;
    },
    addDocumentOptimistic(state, { payload }) {
      const { kbId, document } = payload;
      if (!state.documents[kbId]) state.documents[kbId] = [];
      state.documents[kbId].unshift(document);
    },
    updateDocumentInList(state, { payload }) {
      const doc = normalizeDoc(payload);
      for (const kbId of Object.keys(state.documents)) {
        const idx = state.documents[kbId].findIndex((d) => d.id === doc.id);
        if (idx !== -1) {
          state.documents[kbId][idx] = { ...state.documents[kbId][idx], ...doc };
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // KB list
      .addCase(fetchKbs.pending,   (state)           => { state.kbsStatus = "loading"; })
      .addCase(fetchKbs.fulfilled, (state, { payload }) => { state.kbs = normalizeKbs(payload); state.kbsStatus = "done"; })
      .addCase(fetchKbs.rejected,  (state)           => { state.kbsStatus = "error"; })
      // Create KB
      .addCase(createKb.fulfilled, (state, { payload }) => { state.kbs.push(normalizeKb(payload)); })
      // Update KB
      .addCase(updateKb.fulfilled, (state, { payload }) => {
        const kb = normalizeKb(payload);
        const idx = state.kbs.findIndex((k) => k.id === kb.id);
        if (idx !== -1) state.kbs[idx] = kb;
      })
      // Documents
      .addCase(fetchDocuments.pending, (state, { meta }) => { state.docsStatus[meta.arg] = "loading"; })
      .addCase(fetchDocuments.fulfilled, (state, { payload }) => {
        state.documents[payload.kbId] = normalizeDocs(payload.documents);
        state.docsStatus[payload.kbId] = "done";
      })
      .addCase(fetchDocuments.rejected, (state, { meta }) => { state.docsStatus[meta.arg] = "error"; })
      // URL ingest
      .addCase(ingestUrl.fulfilled, (state, { payload }) => {
        const doc = normalizeDoc(payload.document);
        if (!state.documents[payload.kbId]) state.documents[payload.kbId] = [];
        state.documents[payload.kbId].unshift(doc);
      })
      // Doc status poll
      .addCase(fetchDocumentStatus.fulfilled, (state, { payload }) => {
        const doc = normalizeDoc(payload);
        for (const kbId of Object.keys(state.documents)) {
          const idx = state.documents[kbId].findIndex((d) => d.id === doc.id);
          if (idx !== -1) { state.documents[kbId][idx] = doc; break; }
        }
      })
      // Reingest
      .addCase(reingestDocument.fulfilled, (state, { payload }) => {
        const doc = normalizeDoc(payload);
        for (const kbId of Object.keys(state.documents)) {
          const idx = state.documents[kbId].findIndex((d) => d.id === doc.id);
          if (idx !== -1) { state.documents[kbId][idx] = doc; break; }
        }
      })
      // Delete doc
      .addCase(deleteDocument.fulfilled, (state, { payload }) => {
        const { docId, kbId } = payload;
        if (state.documents[kbId])
          state.documents[kbId] = state.documents[kbId].filter((d) => d.id !== docId);
      })
      // Chunks
      .addCase(fetchChunks.pending, (state, { meta }) => { state.chunksLoading[meta.arg.docId] = true; })
      .addCase(fetchChunks.fulfilled, (state, { payload }) => {
        const { docId, ...rest } = payload;
        state.chunks[docId] = rest;
        state.chunksLoading[docId] = false;
      })
      .addCase(fetchChunks.rejected, (state, { meta }) => { state.chunksLoading[meta.arg.docId] = false; })
      // Chat
      .addCase(sendChat.pending, (state, { meta }) => {
        state.chatLoading = true;
        state.chatError = null;
        // Extract the new user message — it's either .message or the last user turn in .messages
        const content = meta.arg.message
          ?? [...(meta.arg.messages ?? [])].reverse().find(m => m.role === "user")?.content
          ?? "";
        state.chat.push({ role: "user", content, id: `u${Date.now()}` });
      })
      .addCase(sendChat.fulfilled, (state, { payload }) => {
        state.chatLoading = false;
        state.chat.push({
          role: "assistant",
          content: payload.answer,
          citations: payload.citations ?? [],
          id: `a${Date.now()}`,
        });
        if (payload.conversationId) state.conversationId = payload.conversationId;
      })
      .addCase(sendChat.rejected, (state, { payload }) => {
        state.chatLoading = false;
        state.chatError = payload;
        state.chat.push({ role: "error", content: payload, id: `e${Date.now()}` });
      });
  },
});

export const { clearChat, addDocumentOptimistic, updateDocumentInList } = knowledgeSlice.actions;
export default knowledgeSlice.reducer;
